import { ActivatedRoute } from '@angular/router';
import { User } from '@angular/fire/auth';
import { AuthService } from './../services/auth.service';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { getCookie } from '../utils/app.utils';
import { damoAuthConfig } from '../../../damoauth.config';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-redirect',
  imports: [MatCardModule, MatProgressSpinnerModule, TranslatePipe],
  templateUrl: './redirect.component.html',
  styleUrl: './redirect.component.scss',
})
export class RedirectComponent implements OnDestroy, OnInit {
  private static readonly PLATFORM_REDIRECT_DELAY_MS = 3000;

  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly damoAuthConfig = inject(damoAuthConfig);

  protected user!: User;

  private redirectTimer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.redirect();
  }

  ngOnDestroy(): void {
    this.clearRedirectTimer();
  }

  private async redirect(): Promise<void> {
    this.user = await firstValueFrom(this.authService.currentUser$);
    const samlRequest = this.route.snapshot.queryParamMap.get('SAMLRequest');
    const relayState = this.route.snapshot.queryParamMap.get('RelayState');

    if (this.schedulePlatformRedirect(samlRequest)) {
      return;
    }
    if (samlRequest) {
      await this.redirectToSaml(samlRequest, relayState);
    }
  }

  private schedulePlatformRedirect(samlRequest: string | null): boolean {
    const redirectUrl = this.damoAuthConfig.postLoginRedirectUrl;
    if (samlRequest || !redirectUrl) return false;

    this.redirectTimer = setTimeout(() => globalThis.location.assign(redirectUrl), RedirectComponent.PLATFORM_REDIRECT_DELAY_MS);
    return true;
  }

  private async redirectToSaml(samlRequest: string, relayState: string | null): Promise<void> {
    await this.authService.setSessionToken(this.user);
    const fields = this.getSamlFields(samlRequest, relayState);
    const form = this.createSamlForm(fields);
    document.body.appendChild(form);
    form.submit();
  }

  private getSamlFields(samlRequest: string, relayState: string | null): Record<string, string> {
    return {
      SAMLRequest: samlRequest,
      lang: getCookie('lang') || 'en',
      ...(relayState ? { RelayState: relayState } : {}),
    };
  }

  private createSamlForm(fields: Record<string, string>): HTMLFormElement {
    const form = Object.assign(document.createElement('form'), {
      method: 'POST',
      acceptCharset: 'UTF-8',
      target: '_self',
      action: `${this.damoAuthConfig.fns}/utils-auth/saml`,
    });
    this.appendSamlFields(form, fields);
    return form;
  }

  private appendSamlFields(form: HTMLFormElement, fields: Record<string, string>): void {
    for (const [name, value] of Object.entries(fields)) {
      form.appendChild(Object.assign(document.createElement('input'), { type: 'hidden', name, value }));
    }
  }

  private clearRedirectTimer(): void {
    if (this.redirectTimer !== undefined) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = undefined;
    }
  }
}
